"use client";
import { useEffect, useRef } from "react";
import { ChevronLeft, Copy, Gauge, Maximize2, Minimize2, Pause, Play, Repeat2, SkipBack, Square, Trash2 } from "lucide-react";
import { useTimelineStore } from "@/stores/timelineStore";
import { useSceneStore } from "@/stores/sceneStore";
import { useAnimationStore } from "@/stores/animationStore";
import { evaluateCamera, evaluateMovement, findActiveAnimationClip } from "@/utils/evaluateTimeline";
import type { TimelineClip as TimelineClipType, TimelineTrackType } from "@/types/scene";
import { objectRegistry } from "@/runtime/objectRegistry";

const LABEL_WIDTH = 170;
const formatTime = (seconds: number) => {
  const minutes = Math.floor(seconds / 60).toString().padStart(2, "0");
  const value = (seconds % 60).toFixed(3).padStart(6, "0");
  return `${minutes}:${value}`;
};

function TimelineClip({ clip, pixelsPerSecond, trackType }: { clip: TimelineClipType; pixelsPerSecond: number; trackType: TimelineTrackType }) {
  const selected = useTimelineStore((state) => state.selectedClipId === clip.id);
  const selectClip = useTimelineStore((state) => state.selectClip);
  const moveClip = useTimelineStore((state) => state.moveClip);
  const resizeClip = useTimelineStore((state) => state.resizeClip);
  const interaction = useRef<{ x: number; start: number; duration: number; mode: "move" | "resize" } | null>(null);
  const begin = (event: React.PointerEvent, mode: "move" | "resize") => {
    event.stopPropagation();
    event.currentTarget.setPointerCapture(event.pointerId);
    interaction.current = { x: event.clientX, start: clip.startTime, duration: clip.duration, mode };
    selectClip(clip.id);
  };
  const drag = (event: React.PointerEvent) => {
    const state = interaction.current;
    if (!state) return;
    const delta = (event.clientX - state.x) / pixelsPerSecond;
    if (state.mode === "move") moveClip(clip.id, state.start + delta);
    else resizeClip(clip.id, state.duration + delta);
  };
  return <div className={`timeline-clip ${trackType} ${selected ? "selected" : ""}`} style={{ left: clip.startTime * pixelsPerSecond, width: Math.max(24, clip.duration * pixelsPerSecond) }} onPointerDown={(event) => begin(event, "move")} onPointerMove={drag} onPointerUp={() => { interaction.current = null; }} title={`${clip.name} • ${clip.startTime.toFixed(1)}s → ${(clip.startTime + clip.duration).toFixed(1)}s`}>
    <span className="clip-grip">⠿</span><span className="clip-name">{clip.name}</span><span className="clip-duration">{clip.duration.toFixed(1)}s</span>
    <span className="clip-resize" onPointerDown={(event) => begin(event, "resize")} />
  </div>;
}

export function Timeline() {
  const raf = useRef<number | null>(null);
  const previousTime = useRef<number | null>(null);
  const ruler = useRef<HTMLDivElement>(null);
  const objects = useSceneStore((state) => state.objects);
  const tracks = useTimelineStore((state) => state.tracks);
  const currentTime = useTimelineStore((state) => state.currentTime);
  const duration = useTimelineStore((state) => state.duration);
  const playing = useTimelineStore((state) => state.playing);
  const speed = useTimelineStore((state) => state.playbackSpeed);
  const loop = useTimelineStore((state) => state.loop);
  const snap = useTimelineStore((state) => state.snap);
  const pixelsPerSecond = useTimelineStore((state) => state.pixelsPerSecond);
  const selectedClipId = useTimelineStore((state) => state.selectedClipId);
  const togglePlaying = useTimelineStore((state) => state.togglePlaying);
  const stop = useTimelineStore((state) => state.stop);
  const tick = useTimelineStore((state) => state.tick);
  const setCurrentTime = useTimelineStore((state) => state.setCurrentTime);
  const setPlaybackSpeed = useTimelineStore((state) => state.setPlaybackSpeed);
  const toggleLoop = useTimelineStore((state) => state.toggleLoop);
  const setSnap = useTimelineStore((state) => state.setSnap);
  const zoomIn = useTimelineStore((state) => state.zoomIn);
  const zoomOut = useTimelineStore((state) => state.zoomOut);
  const selectClip = useTimelineStore((state) => state.selectClip);
  const deleteClip = useTimelineStore((state) => state.deleteClip);
  const duplicateClip = useTimelineStore((state) => state.duplicateClip);

  useEffect(() => {
    const objectIds = new Set(tracks.filter((track) => track.trackType === "animation").map((track) => track.objectId));
    for (const objectId of objectIds) {
      const clip = findActiveAnimationClip(tracks, objectId, currentTime);
      const playback = useAnimationStore.getState().byObjectId[objectId];
      if (clip?.data.animation) {
        if (!playback?.playing || playback.active !== clip.data.animation) useAnimationStore.getState().playAnimation(objectId, clip.data.animation);
      } else if (playback?.playing) useAnimationStore.getState().stopAnimation(objectId);
    }
    const movingObjectIds = new Set(tracks.filter((track) => track.trackType === "movement").map((track) => track.objectId));
    for (const objectId of movingObjectIds) {
      const movement = evaluateMovement(tracks, objectId, currentTime);
      const runtime = objectRegistry.get(objectId);
      if (!movement || !runtime) continue;
      runtime.position.set(movement.position.x, movement.position.y, movement.position.z);
      if (movement.rotationY !== null) runtime.rotation.y = movement.rotationY;
    }
    const cameraIds = new Set(tracks.filter((track) => track.trackType === "camera").map((track) => track.objectId));
    for (const objectId of cameraIds) {
      const evaluated = evaluateCamera(tracks, objectId, currentTime);
      const runtime = objectRegistry.get(objectId);
      if (!evaluated || !runtime) continue;
      runtime.position.set(evaluated.position.x, evaluated.position.y, evaluated.position.z);
      runtime.rotation.set(evaluated.rotation.x, evaluated.rotation.y, evaluated.rotation.z);
    }
  }, [currentTime, tracks]);

  useEffect(() => {
    if (!playing) { previousTime.current = null; if (raf.current) cancelAnimationFrame(raf.current); return; }
    const frame = (time: number) => {
      if (previousTime.current !== null) tick(Math.min((time - previousTime.current) / 1000, .1));
      previousTime.current = time;
      raf.current = requestAnimationFrame(frame);
    };
    raf.current = requestAnimationFrame(frame);
    return () => { if (raf.current) cancelAnimationFrame(raf.current); };
  }, [playing, tick]);

  const scrub = (event: React.PointerEvent<HTMLDivElement>) => {
    const rect = ruler.current?.getBoundingClientRect();
    if (!rect) return;
    setCurrentTime((event.clientX - rect.left) / pixelsPerSecond);
  };
  const timelineWidth = duration * pixelsPerSecond;
  const markers = Array.from({ length: Math.ceil(duration) + 1 }, (_, index) => index);
  return <section className="timeline-panel">
    <div className="timeline-topbar">
      <strong>Timeline</strong>
      <div className="timeline-transport">
        <button onClick={() => setCurrentTime(0)} title="Go to start"><SkipBack size={14} /></button>
        <button className={playing ? "active" : ""} onClick={togglePlaying} title="Play / Pause (Space)">{playing ? <Pause size={15} /> : <Play size={15} />}</button>
        <button onClick={stop} title="Stop"><Square size={13} /></button>
        <span className="time-readout">{formatTime(currentTime)} <i>/</i> {formatTime(duration)}</span>
      </div>
      <div className="timeline-options">
        <label><Gauge size={13} /><select value={speed} onChange={(event) => setPlaybackSpeed(Number(event.target.value))}>{[.25,.5,1,1.5,2].map((value) => <option key={value} value={value}>{value}x</option>)}</select></label>
        <button className={loop ? "active" : ""} onClick={toggleLoop} title="Loop"><Repeat2 size={14} /></button>
        <label>Snap<select value={snap} onChange={(event) => setSnap(Number(event.target.value))}>{[.1,.25,.5,1].map((value) => <option key={value} value={value}>{value}s</option>)}</select></label>
        <button onClick={zoomOut} title="Zoom out"><Minimize2 size={14} /></button><button onClick={zoomIn} title="Zoom in"><Maximize2 size={14} /></button>
        <button disabled={!selectedClipId} onClick={() => selectedClipId && duplicateClip(selectedClipId)} title="Duplicate clip"><Copy size={14} /></button>
        <button className="danger" disabled={!selectedClipId} onClick={() => selectedClipId && deleteClip(selectedClipId)} title="Delete clip"><Trash2 size={14} /></button>
      </div>
    </div>
    <div className="timeline-scroll">
      <div className="timeline-stage" style={{ width: LABEL_WIDTH + timelineWidth }} onPointerDown={() => selectClip(null)}>
        <div className="timeline-ruler-label"><ChevronLeft size={13} /> Tracks</div>
        <div ref={ruler} className="timeline-ruler" style={{ left: LABEL_WIDTH, width: timelineWidth }} onPointerDown={scrub} onPointerMove={(event) => { if (event.buttons === 1) scrub(event); }}>
          {markers.map((time) => <span key={time} style={{ left: time * pixelsPerSecond }}><i />{time}s</span>)}
        </div>
        <div className="timeline-rows">
          {tracks.length === 0 && <div className="timeline-empty"><span>Select a character and add an animation from the Inspector.</span></div>}
          {tracks.map((track) => {
            const object = objects.find((item) => item.id === track.objectId);
            return <div className="timeline-row" key={track.id}>
              <div className="track-label"><strong>{object?.name ?? "Missing object"}</strong><span>{track.trackType}</span></div>
              <div className="track-lane" style={{ width: timelineWidth }} onPointerDown={(event) => { event.stopPropagation(); const rect = event.currentTarget.getBoundingClientRect(); setCurrentTime((event.clientX - rect.left) / pixelsPerSecond); }}>{track.clips.map((clip) => <TimelineClip key={clip.id} clip={clip} trackType={track.trackType} pixelsPerSecond={pixelsPerSecond} />)}</div>
            </div>;
          })}
        </div>
        <div className="playhead" style={{ left: LABEL_WIDTH + currentTime * pixelsPerSecond }}><span /></div>
      </div>
    </div>
  </section>;
}
